/**
 * WHAT A SHOPPER DOES IN THE SHOP — every shopper, and which ad sent them when one did.
 *
 * DK Store's first seven hours of Meta ads, 2026-09-17: 36 link clicks, 26 landing page views, 28
 * product views — and 0 adds to cart, 0 purchases. Meta can say how many arrived; it cannot say
 * whether they moved through the photos, changed colour, opened another product or went to the
 * shop's home before they left. These steps say that, for visits that came from an ad and no other.
 *
 * THE CONTRACT is stylon-server `modules/ads/journeys/visitor-journey.types.ts`
 * (`ShopJourneyEventPayload`). Each step is one analytics event, `eventType: 'shop_journey'`, in the
 * same batched POST /analytics/events/batch as the shop's other events (api/storeApi.js), with the
 * visitor key the ad click was recorded under — the journey reader ties the two together.
 *
 *   page             every change of page, with its type, path, product and the variant a link names
 *   gallery          a move through a product's photos — swipe, arrow, thumbnail, the full-screen
 *                    viewer opened or moved in — with where it ended; one per 1.5 s per gallery
 *   variant          a colour, size or type chosen, and the variant before it
 *   cart_add         an add to the cart, whether it went in, and the refusal when it did not
 *   checkout_start   the checkout opened on a basket
 *   exit             the page hidden or left: how long it was looked at and how far it was scrolled
 *
 * THE LIMITS:
 *   · only a visit the ad click is remembered for (utils/adVisit.js) — this one, or a return within
 *     seven days — and only on that business's pages;
 *   · a landing that names an ad holds its steps until the shop has loaded and its click is
 *     remembered, so the landing page itself is not lost to the order things arrive in;
 *   · at most 150 steps a page session (a load of the app, until the tab is closed or reloaded);
 *   · nothing personal: ids the shop already publishes, the page's path without its query string
 *     (a search's words stay home), numbers, and a refusal code from a fixed list;
 *   · never from a browser driven by automation (`navigator.webdriver`): a test run or a bot is not
 *     a shopper — the rule utils/shopperSteps.js follows;
 *   · never in a way that can throw into the page.
 */
import { beforeStoreEventsLeave, queueStoreEvent } from '../api/storeApi';
import { adOfLocation, currentAdVisit, isVisitOfAd, onAdVisitRemembered } from './adVisit';
import { isDomainMode } from './domainMode';
import { shopSlugOfPath } from './shopSurface';
import { envOf, sourceOf } from './checkoutTelemetry';
import { getVisitorKey } from './visitorKey';
import { journeyPageOf } from './shopPageOf';
import { movementSetBusiness, movementTap } from './shopMovement';
import { isLite, liteReason } from './deviceLite';

/* ── Whose shop this is ──────────────────────────────────────
 *
 * EVERY SHOPPER, NOT ONLY THE ONES AN AD SENT (2026-09-23). The steps were born for ad visits and
 * gated on the remembered click, so a week of DK shoppers who came by themselves — more than the
 * ads brought — left product views and nothing else: no gallery, no colour, no cart, no exit, and
 * the checkout could not be joined to them. The business a page belongs to is told here by the
 * page that loaded it (hooks/useSalonWebTheme.js); an ad visit still names its click, and a visit
 * without one names none. The server keeps both (analytics.service.ts takes a step with a visitor
 * key), and the ads reader still reads only the steps that name a click.
 */
let business = null;
/** The business whose pages these are — `{ salonId, slug }` — as the page that loaded it says. */
export function journeySetBusiness({ salonId, slug } = {}) {
  business = salonId ? { salonId: String(salonId), slug: slug ? String(slug) : null } : null;
  // The session's env, BEFORE the movement recorder below releases the events it has been holding:
  // the reader joins a session's rows to its environment, so the env has to be the first of them.
  sayEnvForThisSession();
  // The movement recorder (utils/shopMovement.js) keeps the same rule, told once, here.
  try { movementSetBusiness({ salonId, slug }); } catch (_) { /* never into the page */ }
}
/** The visit to send an organic step under, when the address is the known business's page. */
function organicVisitFor(path) {
  if (!business) return null;
  if (!isDomainMode()) {
    const slug = shopSlugOfPath(path);
    if (!business.slug || !slug || slug.toLowerCase() !== business.slug.toLowerCase()) return null;
  }
  return { salonId: business.salonId, clickId: null, campaignId: null };
}

export const JOURNEY_EVENT_TYPE = 'shop_journey';
export const MAX_JOURNEY_STEPS = 150;
export const GALLERY_STEP_MS = 1500;
/** The longest look at a page the server keeps (stylon-server ads/journeys/shop-journey-step.ts): a day. */
const DWELL_MAX_MS = 24 * 60 * 60 * 1000;
/** The most photos a gallery step may count or point at, as the server keeps it. */
const PHOTOS_MAX = 1000;

/** `ShopJourneyKind`, `ShopJourneyPageType` and `galleryAction`, as the contract spells them. */
export const JOURNEY_KINDS = ['page', 'gallery', 'variant', 'cart_add', 'checkout_start', 'exit'];
export const JOURNEY_PAGE_TYPES = ['product', 'shop_home', 'category', 'brand', 'collection', 'search', 'cart', 'checkout', 'other'];
export const GALLERY_ACTIONS = ['swipe', 'arrow', 'thumb', 'viewer_open', 'viewer_move'];
/** Why an add did not go in. */
export const CART_ADD_REFUSALS = ['choice_required', 'out_of_stock', 'stock_cap'];

/** How many steps a landing may hold while its click is being remembered. */
const MAX_HELD = 20;

/* ── Which page: utils/shopPageOf.js, re-exported so nothing that imports it from here moves ── */
export { journeyPageOf };

/* ── The page session ─────────────────────────────────────────────────── */

let seq = 0;
let held = [];
let holding = false;
const galleries = new Map();
/** The page being looked at: its address, and how long and how far it has been looked at. */
let page = null;
let watching = false;
let stopWatching = null;
let stopListening = null;

const now = () => Date.now();
const automated = () => typeof navigator !== 'undefined' && navigator.webdriver === true;
const currentPath = () => (typeof window !== 'undefined' && window.location ? window.location.pathname : '');

/** Whether the address a step was taken on is a page of the business the visit's ad was for. */
function isVisitsBusiness(visit, path) {
  if (isDomainMode()) return true;
  const slug = shopSlugOfPath(path);
  return !!visit.slug && slug.toLowerCase() === String(visit.slug).toLowerCase();
}

/**
 * ONCE A PAGE SESSION, FOR EVERY PAGE SESSION: what the shopper holds and where they came from.
 *
 * FOR EVERY ONE IS THE FIX (2026-10-14, the owner: "make sure we have full coverage of movements in
 * the website"). This rode out with the first journey STEP, and a step is only sent once the page
 * has said whose it is — so a session that opens a page and does nothing at all sent no env.
 * Measured over 10-13 and 10-14: 36 of 133 sessions had none, and their device, their in-app state
 * and their source were therefore unreadable. Most of them look like the in-app browser fetching
 * the page before anyone chose to look at it, which is the very thing worth being able to name.
 *
 * `hidden` and `prerender` are what make it nameable: a page that was never on screen, and a
 * browser saying outright that it is prerendering. Neither says anything about the person.
 */
let envSaid = false;
export const ENV_EVENT_TYPE = 'shop_env';
function sayEnv(visit) {
  if (envSaid || !visit || !visit.salonId) return;
  envSaid = true;
  try {
    queueStoreEvent({
      source: 'store',
      eventType: ENV_EVENT_TYPE,
      salonId: visit.salonId,
      visitorKey: getVisitorKey() || undefined,
      payload: {
        ...envOf(),
        ...sourceOf(),
        ad: !!visit.clickId,
        hidden: typeof document !== 'undefined' && document.visibilityState === 'hidden',
        prerender: typeof document !== 'undefined' && !!document.prerendering,
        /* WHETHER THIS PHONE GOT THE LITE STORE (utils/deviceLite, 2026-11-03), and the signal that decided it — so
           production can say how many shoppers are lite, and whether it is the old phones that are. The decision as
           it stood when the shop loaded: a phone the boot frames turn lite later in the visit says so from its next
           session (the verdict is remembered). `liteBy: 'override'` with `lite: false` is a phone switched off by
           `?lite=off` — a test device, as a rule. */
        lite: isLite(),
        ...(liteReason() ? { liteBy: liteReason() } : {}),
      },
    });
  } catch (_) { /* never into the page */ }
}

/**
 * The page has said whose it is: say the session's env now, step or no step. Under the ad's visit
 * when this page is that ad's business's, else under the business's own — the rule `emit` keeps,
 * so a session on another shop's pages still says nothing.
 */
function sayEnvForThisSession() {
  try {
    if (envSaid || !business) return;
    const path = currentPath();
    const visit = currentAdVisit();
    const ofAd = visit && visit.clickId && isVisitsBusiness(visit, path) ? visit : null;
    sayEnv(ofAd || organicVisitFor(path));
  } catch (_) { /* never into the page */ }
}
function send(step, visit) {
  sayEnv(visit);
  queueStoreEvent({
    source: 'store',
    eventType: JOURNEY_EVENT_TYPE,
    salonId: visit.salonId,
    visitorKey: getVisitorKey() || undefined,
    payload: { ...step, adClickId: visit.clickId, campaignId: visit.campaignId || null },
  });
}

/** Steps held for a landing, sent the moment its click is remembered — those of its business. */
function release(visit) {
  if (!holding) return;
  holding = false;
  const waiting = held;
  held = [];
  waiting.filter((step) => isVisitsBusiness(visit, step.path)).forEach((step) => send(step, visit));
}

/**
 * Number a step and send it, hold it or drop it. `fields` are the contract's own; nothing else is
 * ever sent.
 */
function emit(kind, fields = {}) {
  try {
    if (automated() || seq >= MAX_JOURNEY_STEPS) return;
    const visit = currentAdVisit();
    const landing = typeof window !== 'undefined' ? adOfLocation(window.location.search) : null;
    // A landing whose click is not remembered yet holds everything until it is — the shop's payload
    // arrives after the page's first steps, and the click is recorded from it.
    if (landing && !isVisitOfAd(visit, landing)) {
      holding = true;
      if (!stopListening) stopListening = onAdVisitRemembered(release);
    }
    let under = visit && isVisitsBusiness(visit, currentPath()) ? visit : null;
    if (!holding && !under) under = organicVisitFor(currentPath());
    if (!holding && !under) return;
    if (holding && held.length >= MAX_HELD) return;
    seq += 1;
    const step = pickContractFields({ kind, at: now(), path: currentPath(), ...fields, seq });
    if (holding) held.push(step);
    else send(step, under);
  } catch (_) {
    /* never throw from instrumentation */
  }
}

const CONTRACT_FIELDS = [
  'kind', 'at', 'seq', 'path', 'pageType', 'productId', 'variantId', 'galleryAction', 'photoIndex',
  'photoCount', 'fromVariantId', 'ok', 'errorCode', 'dwellMs', 'maxScrollPct',
];

function pickContractFields(step) {
  const out = {};
  CONTRACT_FIELDS.forEach((key) => { if (step[key] !== undefined) out[key] = step[key]; });
  return out;
}

/* ── How long, and how far ────────────────────────────────────────────── */

function scrolledPct() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const doc = document.documentElement;
  const height = Math.max(doc?.scrollHeight || 0, document.body?.scrollHeight || 0);
  if (!height) return 0;
  const seen = (window.scrollY || window.pageYOffset || 0) + (window.innerHeight || 0);
  return Math.max(0, Math.min(100, Math.round((seen / height) * 100)));
}

function onScroll() {
  if (page) page.maxScrollPct = Math.max(page.maxScrollPct, scrolledPct());
}

/**
 * The page is being left or hidden: say how long it was looked at and how far it went, once. Asked
 * by the shop's event queue as it goes (api/storeApi.js), so the step leaves in its closing request.
 */
function onHidden() {
  if (!page || page.exitSent) return;
  if (page.visibleSince != null) {
    page.visibleMs += now() - page.visibleSince;
    page.visibleSince = null;
  }
  page.exitSent = true;
  // A photo move still waiting out its 1.5 s is not sent early: the limit holds to the end.
  galleries.forEach((gallery) => clearTimeout(gallery.timer));
  galleries.clear();
  emit('exit', {
    pageType: page.pageType,
    productId: page.productId,
    variantId: page.variantId,
    dwellMs: Math.min(DWELL_MAX_MS, Math.max(0, Math.round(page.visibleMs))),
    maxScrollPct: Math.max(page.maxScrollPct, scrolledPct()),
  });
}

/** The page is looked at again: its clock runs again, and a later hide says so again. */
function onVisibility() {
  if (typeof document === 'undefined' || document.visibilityState === 'hidden') return;
  if (page && page.visibleSince == null) {
    page.visibleSince = now();
    page.exitSent = false;
  }
}

function watchThePage() {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
  stopWatching = beforeStoreEventsLeave(onHidden);
}

/* ── The steps ────────────────────────────────────────────────────────── */

/** The address changed (or a page came back from the browser's back-forward cache): a new page. */
export function journeyPageChange({ pathname, search } = {}) {
  try {
    galleries.forEach((gallery) => clearTimeout(gallery.timer));
    galleries.clear();
    const where = journeyPageOf(pathname, search);
    const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
    page = where
      ? { ...where, visibleSince: visible ? now() : null, visibleMs: 0, maxScrollPct: 0, exitSent: false }
      : null;
    if (!where) return;
    watchThePage();
    emit('page', where);
    onScroll();
  } catch (_) {
    /* never throw from instrumentation */
  }
}

/**
 * The shopper moved through a gallery's photos. `gallery` names it (the product page's photos, its
 * viewer, a card), so two galleries do not share a limit. At most one step per 1.5 s each: the
 * first move is sent at once, and the last one inside the 1.5 s — where the shopper ended up —
 * when they are up.
 */
export function journeyGallery(gallery, { action, photoIndex, photoCount, productId, variantId } = {}) {
  try {
    if (!GALLERY_ACTIONS.includes(action) || !page) return;
    // Every move is a tap of the movement record (no 1.5 s limit there: it has its own cap per page).
    movementTap(`gallery_${action}`, { detail: gallery, productId: productId ?? page.productId, variantId: variantId ?? page.variantId });
    const step = {
      galleryAction: action,
      photoIndex: Number.isInteger(photoIndex) && photoIndex >= 0 ? Math.min(PHOTOS_MAX, photoIndex) : undefined,
      photoCount: Number.isInteger(photoCount) && photoCount >= 0 ? Math.min(PHOTOS_MAX, photoCount) : undefined,
      productId: productId ?? page.productId ?? null,
      variantId: variantId ?? page.variantId ?? null,
      pageType: page.pageType,
    };
    const key = String(gallery || 'gallery');
    const state = galleries.get(key) || { lastAt: -Infinity, timer: null, latest: null };
    galleries.set(key, state);
    const at = now();
    if (at - state.lastAt >= GALLERY_STEP_MS) {
      state.lastAt = at;
      emit('gallery', step);
      return;
    }
    state.latest = step;
    if (state.timer) return;
    state.timer = setTimeout(() => {
      state.timer = null;
      const latest = state.latest;
      state.latest = null;
      if (!latest) return;
      state.lastAt = now();
      emit('gallery', latest);
    }, GALLERY_STEP_MS - (at - state.lastAt));
  } catch (_) {
    /* never throw from instrumentation */
  }
}

/** A colour, size or type was chosen and the variant changed. */
export function journeyVariant({ productId, variantId, fromVariantId }) {
  if (!page) return;
  emit('variant', { pageType: page.pageType, productId: productId ?? null, variantId: variantId ?? null, fromVariantId: fromVariantId ?? null });
}

/** An add to the cart — `ok` when something went in; `errorCode` from `CART_ADD_REFUSALS` when not. */
export function journeyCartAdd({ productId, variantId, ok, errorCode }) {
  if (!page) return;
  emit('cart_add', {
    pageType: page.pageType,
    productId: productId ?? null,
    variantId: variantId ?? null,
    ok: !!ok,
    errorCode: ok ? null : (CART_ADD_REFUSALS.includes(errorCode) ? errorCode : null),
  });
}

/** The checkout opened on a basket. */
export function journeyCheckoutStart() {
  if (!page) return;
  emit('checkout_start', { pageType: page.pageType });
}

/** Tests only: a fresh page session. */
export function __resetShopJourney() {
  galleries.forEach((gallery) => clearTimeout(gallery.timer));
  galleries.clear();
  business = null;
  envSaid = false;
  seq = 0;
  held = [];
  holding = false;
  page = null;
  if (watching && typeof window !== 'undefined') {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('visibilitychange', onVisibility);
  }
  if (stopWatching) stopWatching();
  stopWatching = null;
  watching = false;
  if (stopListening) stopListening();
  stopListening = null;
}
